import type { ServicePageConfig } from "../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../package-pricing";
import { getCityMunicipalityList } from "../../cities";

const stockholmKommunerText = getCityMunicipalityList("stockholm");

export const jarfallaPages: ServicePageConfig[] = [
  {
    slug: "bortforsling-jarfalla",
    serviceCategory: "bortforsling",
    city: "stockholm",

    meta: {
      title: "Bortforsling Järfälla – Snabb Hämtning | Ungdomskraft",
      description:
        "Boka bortforsling i Järfälla. Vi hämtar möbler, vitvaror och skräp i Jakobsberg, Kallhäll, Barkarby, Viksjö, Skälby och Stäket. Fast pris, ofta samma dag.",
      h1: "Bortforsling Järfälla",
    },

    hero: {
      heading: "Bortforsling i Järfälla",
      intro:
        "Boka bortforsling i Järfälla – vi hämtar möbler, vitvaror och skrymmande föremål i hela kommunen, från lägenheterna i Jakobsberg och nya Barkarbystaden till villorna i Viksjö, Skälby och Stäket. Snabb bokning, fast pris och ofta tillgängliga redan samma dag. Över 50 000 slutförda uppdrag i Stockholmsregionen.",
      heroImage: "/hero-bortforsling.webp",
      heroImageAlt:
        "Ungdomskraft hämtar möbler vid bortforsling i Järfälla – ung medarbetare lastar skåpbil",
    },

    itemsSection: {
      heading: "Saker vi bortforslar i Järfälla",
      items: [
        {
          name: "Återvinning",
          description: "Glas, metall, papper och annat återvinningsbart",
          image: "/recycling-bin.webp",
          alt: "Återvinning som hämtas vid bortforsling i Järfälla och körs till Görväln ÅVC",
        },
        {
          name: "Kartonger",
          description: "Flyttkartonger och wellpappkartonger",
          image: "/box.webp",
          alt: "Flyttkartonger som forslas bort från villa i Viksjö, Järfälla",
        },
        {
          name: "Möbler",
          description: "Vardagsrum, sovrum, kontor och andra hemmöbler",
          image: "/furniture.webp",
          alt: "Soffor och hemmöbler vid bortforsling i Jakobsberg, Järfälla",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, ljudsystem och annan elektronik",
          image: "/electronics.webp",
          alt: "Elektronik och TV-apparater som bortforslas i Barkarbystaden, Järfälla",
        },
        {
          name: "Vitvaror",
          description:
            "Kylskåp, tvättmaskiner, torktumlare och andra vitvaror",
          image: "/appliances.webp",
          alt: "Kylskåp och vitvaror som hämtas vid bortforsling i Kallhäll, Järfälla",
        },
        {
          name: "Träningsutrustning",
          description:
            "Löpband, cyklar, vikter och annan träningsutrustning",
          image: "/fitness-equipment.webp",
          alt: "Löpband och träningsutrustning vid bortforsling i Skälby, Järfälla",
        },
      ],
    },

    seoContent: {
      mainHeading: "Bortforsling i Järfälla – snabbt, smidigt och ansvarsfullt",
      mainText:
        "Järfälla är en kommun i stark utveckling, med fyra kommundelar – Jakobsberg, Kallhäll-Stäket, Viksjö och Barkarby-Skälby. Bebyggelsen är blandad: det täta centrumet i Jakobsberg, de stora villaområdena i Viksjö och Skälby, samt nya Barkarbystaden som växer fram på det gamla flygfältet med tusentals nya lägenheter och en kommande tunnelbanestation. Den blandningen gör bortforsling till olika utmaningar beroende på var du bor: i lägenheten handlar det ofta om trapphus och hiss, medan det i villan snarare handlar om ett fullt garage eller ett källarförråd. Med Ungdomskraft slipper du allt krångel oavsett vilket. Vi skickar unga, starka medarbetare med skåpbil direkt till din adress.\n\nVårt team hanterar hela kedjan: vi bär ut föremålen från din lägenhet, villa eller ditt förråd, lastar i bilen och kör till närmaste återvinningscentral. Järfälla har en stor fördel här – kommunens egen återvinningscentral, Görväln ÅVC vid Återvinningsvägen nära Mälaren, ligger mitt i kommunen och drivs av SÖRAB som Järfälla är delägare i. Det gör att avfallet sällan behöver köras långt. Du behöver inte tänka på sortering, parkering eller tunga lyft. Vi tar hand om allt.\n\nTack vare att E18 går rakt genom Järfälla och Mälarbanans tre pendeltågsstationer – Barkarby, Jakobsberg och Kallhäll – binder ihop kommunen tar vi oss snabbt mellan kommundelarna. Det gör att vi ofta kan vara på plats redan samma dag du bokar. Våra medarbetare är vana vid både de stora bostadsgårdarna i Jakobsberg och de smala villagatorna i Viksjö och Skälby – och har utrustningen som krävs: bärbälten, skyddsfiltar och lastramper som skyddar både dina ägodelar och trapphuset under hela processen.",
      preparationHeading: "Förbered inför din bortforsling i Järfälla",
      preparationText:
        "Du behöver egentligen inte göra mycket alls – våra ungdomar sköter det tunga jobbet. Men vill du snabba på processen och hålla nere kostnaden finns det några enkla saker du kan göra:\n\nRensa en fri väg (ungefär 1 meter bred) från entrén till föremålen som ska bort. Bor du i villa i Viksjö, Skälby eller Stäket är det smidigt om vi kan backa in på uppfarten – meddela oss om grinden eller garageporten behöver öppnas. Bor du i lägenhet i Jakobsberg, Kallhäll eller Barkarbystaden, kontrollera om det finns en lastzon, innergård eller gästparkering nära porten som vi kan använda för lastning. Det sparar tid jämfört med att leta plats på gatan.\n\nSamla gärna sakerna på ett ställe om möjligt, till exempel i hallen, garaget eller förrådet. Du behöver inte demontera möbler – det ingår i vår tjänst om det behövs. Farligt avfall som färgrester, batterier och kemikalier kan vi tyvärr inte ta med, men sådant lämnar du gratis som Järfällabo på Görväln ÅVC.",
      additionalSections: [
        {
          heading: "Kommundelar och områden vi täcker i Järfälla",
          content:
            "Vi utför bortforsling i hela Järfälla kommun – från de etablerade villakvarteren till de helt nya stadsdelarna. Oavsett om du bor i en lägenhet i Jakobsberg, en villa i Viksjö, ett radhus i Skälby eller en nybyggd etta i Barkarbystaden kan vi vara på plats, ofta redan samma dag.\n\nI Jakobsberg, kommunens centralort, har vi stor vana vid lägenhetshämtningar med hiss och trapphus, nära Jakobsbergs centrum och pendeltågsstationen, samt i miljonprogramsområden som Söderhöjden. I Barkarby och Barkarbystaden hanterar vi både den äldre villabebyggelsen och de många nybyggda lägenheterna kring den nya tunnelbanestationen.\n\nVi är minst lika snabba i kommunens norra och västra delar: villaområdena i Viksjö och Skälby, Kallhäll med blandad bebyggelse vid pendeltåget, samt Stäket och områdena ner mot Mälaren och Görvälns naturreservat. Tack vare närheten till E18, Mälarbanan och de tre pendeltågsstationerna planerar vi alltid rutten i förväg så att du slipper vänta. Inga extra avgifter för någon kommundel.",
        },
        {
          heading: "Återvinning och miljöansvar i Järfälla",
          content:
            "En viktig del av vår bortforsling i Järfälla är att allt hanteras på ett miljövänligt sätt. Vi sorterar materialet och kör det till godkända återvinningscentraler i området. Järfälla är en av delägarkommunerna i SÖRAB, och de anläggningar vi använder mest för uppdrag i kommunen är:\n\nGörväln ÅVC vid Återvinningsvägen – kommunens egen återvinningscentral nära Mälaren och Görvälns naturreservat, där sorterat grovavfall, trädgårdsavfall, elavfall och farligt avfall tas emot, med en särskild återbrukscontainer. SÖRAB:s mobila återvinningscentral – som ställs upp nära där du bor för mindre mängder. Vi väljer alltid den lösning som ligger närmast din adress för att hålla restiden – och därmed kostnaden – nere.\n\nDet som kan återanvändas skänker vi vidare till organisationer som Myrorna och Stadsmissionen. På så sätt bidrar din bortforsling till cirkulär ekonomi – sakerna får ett nytt liv istället för att hamna på tippen.\n\nOm sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
        },
        {
          heading: "Varför boka bortforsling i Järfälla med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal kunskap –",
              text: "Vi känner Järfällas kommundelar, från bostadsgårdarna i Jakobsberg till villagatorna i Viksjö och de nya kvarteren i Barkarbystaden, och vet att Görväln ÅVC ligger nära. Det gör att vi planerar effektiva rutter och håller nere kostnaden för dig.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har skåpbilar och personal i hela Stockholmsregionen och E18 går rakt genom Järfälla. Ofta kan vi vara på plats redan samma dag du bokar – även på kvällar och helger.",
            },
            {
              label: "Fast pris utan överraskningar –",
              text: "Du ser priset direkt vid bokning. Inga dolda avgifter, inga tilläggsavgifter för någon kommundel i Järfälla, och inga offertförfrågningar. Boka på två minuter.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg Hansa. Ditt trapphus, din villauppfart, dina väggar och dina ägodelar är skyddade under hela processen.",
            },
            {
              label: "Miljövänlig hantering –",
              text: "Vi sorterar och återvinner allt vi kan via Görväln ÅVC och SÖRAB:s anläggningar. Det som går att donera skänks vidare. Du kan ha rätt till RUT-avdrag för transport till återbruk.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar bortforsling i Järfälla?",
        answer:
          "Priset beror på volym, vikt och tid. Vår minsta kategori (Extra Liten) startar på 249 kr i grundavgift, och ett genomsnittligt uppdrag kostar runt 499–2 495 kr beroende på mängden. Du ser det exakta priset direkt vid bokning – inga dolda avgifter och inga tillägg för någon kommundel i Järfälla.",
      },
      {
        question: "Kan jag få RUT-avdrag på bortforsling i Järfälla?",
        answer:
          "Om sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Hur snabbt kan ni komma för bortforsling i Järfälla?",
        answer:
          "Vi har skåpbilar och personal i hela Stockholmsregionen och E18 går rakt genom Järfälla, så vi tar oss snabbt mellan kommundelarna. Ofta kan vi vara på plats redan samma dag, och vid akuta behov ibland inom 60 minuter. Boka online och välj den tid som passar dig.",
      },
      {
        question: "Ingår det en skåpbil vid bortforsling i Järfälla?",
        answer:
          "Ja, vi kommer alltid med skåpbil och personal. Du behöver inte ordna eget fordon – vi sköter allt från bärning till transport och bortkörning till närmaste återvinningscentral.",
      },
      {
        question: "Kan ni bära ner saker från lägenhet i trappa i Järfälla?",
        answer:
          "Absolut. Vi är vana vid både trapphus och hissar i flerbostadshusen kring Jakobsberg, Kallhäll och Barkarbystaden. Vi bär ner möbler, vitvaror och annat oavsett våning – inga extra avgifter för trappor.",
      },
      {
        question: "Hämtar ni från villa och garage i Järfälla?",
        answer:
          "Ja, en stor del av våra uppdrag i Järfälla är villahämtningar i Viksjö, Skälby, Barkarby och Stäket. Vi backar in på uppfarten där det går och tömmer garage, källare och förråd. Meddela oss bara om en grind eller garageport behöver öppnas.",
      },
      {
        question: "Kan ni demontera möbler innan bortforsling?",
        answer:
          "Ja, vi demonterar möbler på plats om det behövs för att få ut dem genom dörrar eller nedför trappor. Det ingår i tjänsten utan extra kostnad.",
      },
      {
        question: "Är bortforslingen i Järfälla försäkrad?",
        answer:
          "Ja, alla uppdrag är försäkrade genom Trygg Hansa. Det innebär att din fastighet, ditt trapphus, din villauppfart och dina ägodelar är skyddade under hela processen – från lyft till transport.",
      },
      {
        question: "Vart kör ni avfallet efter bortforsling i Järfälla?",
        answer:
          "Vi sorterar allt material och kör det till närmaste godkända återvinningscentral. Järfälla är en av delägarkommunerna i SÖRAB, så det blir oftast Görväln ÅVC vid Mälaren. Det som kan återanvändas skänks till Myrorna eller Stadsmissionen.",
      },
      {
        question: "Kan jag boka bortforsling i Järfälla på helgen?",
        answer:
          "Ja, vi erbjuder bortforsling alla dagar i veckan, inklusive lördagar och söndagar. Välj det datum och den tid som passar dig bäst direkt i vår bokning.",
      },
      {
        question: "Kan ni hämta saker från källare eller förråd i Järfälla?",
        answer:
          "Ja, vi hämtar föremål från alla utrymmen – källare, förråd, vindsutrymmen och garage. Vi är vana vid både villaförråden i Viksjö och lägenhetsförråden i Jakobsberg och Barkarbystaden.",
      },
      {
        question: "Kan ni göra flera stopp vid bortforsling i Järfälla?",
        answer:
          "Ja, du kan lägga till flera hämtadresser i samma bokning. Det är praktiskt om du till exempel behöver hämta saker från både din lägenhet i Jakobsberg och ditt förråd på en annan adress i kommunen.",
      },
      {
        question: "Vilka kommundelar i Järfälla täcker ni?",
        answer:
          "Vi utför bortforsling i hela Järfälla – Jakobsberg, Kallhäll, Stäket, Viksjö, Barkarby, Barkarbystaden och Skälby. Inga extra avgifter för någon kommundel.",
      },
      {
        question: "Tar ni hand om farligt avfall vid bortforsling i Järfälla?",
        answer:
          "Vi kan tyvärr inte transportera farligt avfall som färg, kemikalier, batterier eller asbest. Sådant lämnar du gratis som Järfällabo på Görväln ÅVC.",
      },
      {
        question: "Vilka kommuner runt Järfälla utför ni bortforsling i?",
        answer: `Vi täcker hela Stockholmsregionen, inklusive följande kommuner: ${stockholmKommunerText}. Inga extra avgifter för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Boka din bortforsling i Järfälla i 3 enkla steg",
      step2Text:
        "Våra ungdomar i Järfälla kommer till dig och hämtar det du vill ha bortforslat. Vi hanterar tunga lyft, trånga trapphus, smala hissar och fulla villagarage – du behöver inte lyfta ett finger.",
      ctaHeading: "Boka bortforsling i Järfälla idag",
      ctaText:
        "Fyll i formuläret och få pris direkt – inga offertförfrågningar. Smidig bortforsling i hela Järfälla kommun.",
    },

    en: {
      slug: "junk-removal-jarfalla",
      meta: {
        title: "Junk Removal Järfälla – Fast Pickup | Ungdomskraft",
        description:
          "Book junk removal in Järfälla. We pick up furniture, appliances and bulky items in Jakobsberg, Kallhäll, Barkarby, Viksjö, Skälby and Stäket. Fixed price, often same day.",
        h1: "Junk Removal Järfälla",
      },
      hero: {
        heading: "Junk Removal in Järfälla",
        intro:
          "Book junk removal in Järfälla – we collect furniture, appliances and bulky items across the whole municipality, from the apartments in Jakobsberg and the new Barkarbystaden to the villas in Viksjö, Skälby and Stäket. Quick booking, fixed price and often available the same day. Over 50,000 completed jobs in the Stockholm region.",
        heroImageAlt:
          "Ungdomskraft collecting furniture during junk removal in Järfälla – young worker loading a van",
      },
      itemsSection: {
        heading: "Items we remove in Järfälla",
        items: [
          {
            name: "Recycling",
            description: "Glass, metal, paper and other recyclables",
            image: "/recycling-bin.webp",
            alt: "Recyclables collected during junk removal in Järfälla and driven to Görväln recycling centre",
          },
          {
            name: "Boxes",
            description: "Moving boxes and cardboard boxes",
            image: "/box.webp",
            alt: "Moving boxes removed from a villa in Viksjö, Järfälla",
          },
          {
            name: "Furniture",
            description: "Living room, bedroom, office and other home furniture",
            image: "/furniture.webp",
            alt: "Sofas and home furniture during junk removal in Jakobsberg, Järfälla",
          },
          {
            name: "Electronics",
            description: "TVs, computers, sound systems and other electronics",
            image: "/electronics.webp",
            alt: "Electronics and TVs removed in Barkarbystaden, Järfälla",
          },
          {
            name: "Appliances",
            description: "Fridges, washing machines, dryers and other appliances",
            image: "/appliances.webp",
            alt: "Fridges and appliances collected during junk removal in Kallhäll, Järfälla",
          },
          {
            name: "Fitness Equipment",
            description: "Treadmills, bikes, weights and other exercise equipment",
            image: "/fitness-equipment.webp",
            alt: "Treadmill and fitness equipment during junk removal in Skälby, Järfälla",
          },
        ],
      },
      seoContent: {
        mainHeading: "Junk removal in Järfälla – fast, hassle-free and responsible",
        mainText:
          "Järfälla is a fast-developing municipality with four districts – Jakobsberg, Kallhäll-Stäket, Viksjö and Barkarby-Skälby. The housing is mixed: the dense centre of Jakobsberg, the large villa areas of Viksjö and Skälby, and the new Barkarbystaden rising on the old airfield with thousands of new apartments and a coming metro station. That mix makes junk removal a different challenge depending on where you live: in an apartment it is often stairwells and elevators, while in a villa it is more about a full garage or a basement storage room. With Ungdomskraft, you skip the hassle either way. We send young, strong workers with a van directly to your address.\n\nOur team handles the entire chain: we carry items out of your apartment, villa or storage unit, load them into the van and drive to the nearest recycling centre. Järfälla has a big advantage here – the municipality's own recycling centre, Görväln ÅVC on Återvinningsvägen near Lake Mälaren, is located in the middle of the municipality and run by SÖRAB, of which Järfälla is a part-owner. This means the waste rarely has to be driven far. You don't need to worry about sorting, parking or heavy lifting. We take care of everything.\n\nBecause the E18 runs straight through Järfälla and the Mälarbanan railway's three commuter stations – Barkarby, Jakobsberg and Kallhäll – tie the municipality together, we move quickly between districts. This means we can often be on site the same day you book. Our workers are used to both the large residential courtyards of Jakobsberg and the narrow villa streets of Viksjö and Skälby – and have the right equipment: carrying belts, protective blankets and loading ramps that protect both your belongings and the stairwell throughout the process.",
        preparationHeading: "Preparing for your junk removal in Järfälla",
        preparationText:
          "You don't really need to do much at all – our young workers handle the heavy work. But if you want to speed things up and keep the cost down, there are a few simple things you can do:\n\nClear a path (about 1 metre wide) from the entrance to the items that need to go. If you live in a villa in Viksjö, Skälby or Stäket, it helps if we can reverse onto the driveway – let us know if a gate or garage door needs opening. If you live in an apartment in Jakobsberg, Kallhäll or Barkarbystaden, check whether there is a loading zone, courtyard or visitor parking near the entrance we can use. It saves time compared to searching for a spot on the street.\n\nGather the items in one place if possible, for example in the hallway, garage or storage unit. You don't need to disassemble furniture – we include that in our service if needed. We cannot take hazardous waste such as paint, batteries and chemicals, but as a Järfälla resident you can drop those off for free at Görväln recycling centre.",
        additionalSections: [
          {
            heading: "Districts and areas we cover in Järfälla",
            content:
              "We perform junk removal across the entire municipality of Järfälla – from the established villa neighbourhoods to the brand-new districts. Whether you live in an apartment in Jakobsberg, a villa in Viksjö, a townhouse in Skälby or a new studio in Barkarbystaden, we can be on site, often the same day.\n\nIn Jakobsberg, the municipality's main town, we are highly experienced with apartment pickups via elevator and stairwell, close to Jakobsberg centre and the commuter-rail station, as well as in 'miljonprogram' areas such as Söderhöjden. In Barkarby and Barkarbystaden we handle both the older villa housing and the many newly built apartments around the new metro station.\n\nWe are just as quick in the northern and western parts of the municipality: the villa areas of Viksjö and Skälby, Kallhäll with its mixed housing by the commuter rail, and Stäket and the areas down towards Lake Mälaren and the Görväln nature reserve. Thanks to the proximity of the E18, the Mälarbanan railway and the three commuter-rail stations, we always plan the route in advance so you don't have to wait. No extra charges for any district.",
          },
          {
            heading: "Recycling and environmental responsibility in Järfälla",
            content:
              "An important part of our junk removal in Järfälla is that everything is handled in an environmentally responsible way. We sort the materials and drive them to approved recycling centres in the area. Järfälla is one of the part-owner municipalities of SÖRAB, and the facilities we use most for jobs in the municipality are:\n\nGörväln ÅVC on Återvinningsvägen – the municipality's own recycling centre near Lake Mälaren and the Görväln nature reserve, which receives sorted bulky waste, garden waste, e-waste and hazardous waste, with a dedicated reuse container. SÖRAB's mobile recycling centre – set up near where you live for smaller amounts. We always choose the solution nearest your address to keep travel time – and therefore the cost – down.\n\nItems that can be reused are donated to organisations like Myrorna and Stadsmissionen. This way, your junk removal contributes to a circular economy – items get a new life instead of ending up in landfill.\n\nIf the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
          },
          {
            heading: "Why book junk removal in Järfälla with Ungdomskraft?",
            listItems: [
              {
                label: "Local knowledge –",
                text: "We know Järfälla's districts, from the residential courtyards of Jakobsberg to the villa streets of Viksjö and the new quarters of Barkarbystaden, and that Görväln ÅVC is close by. This lets us plan efficient routes and keep the cost down for you.",
              },
              {
                label: "Fast availability –",
                text: "We have vans and staff across the Stockholm region and the E18 runs straight through Järfälla. We can often be on site the same day you book – including evenings and weekends.",
              },
              {
                label: "Fixed price with no surprises –",
                text: "You see the price immediately when booking. No hidden fees, no surcharges for any district in Järfälla, and no quote requests. Book in two minutes.",
              },
              {
                label: "Insured and safe –",
                text: "All jobs are insured through Trygg Hansa. Your stairwell, your driveway, your walls and your belongings are protected throughout the entire process.",
              },
              {
                label: "Eco-friendly handling –",
                text: "We sort and recycle everything we can via Görväln ÅVC and SÖRAB's facilities. Items suitable for donation are passed on. You may be entitled to a RUT tax deduction for transport to reuse.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does junk removal in Järfälla cost?",
          answer:
            "The price depends on volume, weight and time. Our smallest category (Extra Liten) starts at 249 kr base fare, and an average job costs around 499–1,699 kr depending on the amount. You see the exact price immediately when booking – no hidden fees and no surcharges for any district in Järfälla.",
        },
        {
          question: "Can I get a RUT tax deduction on junk removal in Järfälla?",
          answer:
            "If the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
        },
        {
          question: "How quickly can you come for junk removal in Järfälla?",
          answer:
            "We have vans and staff across the Stockholm region and the E18 runs straight through Järfälla, so we move quickly between districts. We can often be on site the same day, and for urgent needs sometimes within 60 minutes. Book online and choose the time that suits you.",
        },
        {
          question: "Is a van included with junk removal in Järfälla?",
          answer:
            "Yes, we always come with a van and crew. You don't need to arrange your own vehicle – we handle everything from carrying to transport and disposal at the nearest recycling centre.",
        },
        {
          question: "Can you carry items down from an apartment via stairs in Järfälla?",
          answer:
            "Absolutely. We are used to both stairwells and elevators in the apartment buildings around Jakobsberg, Kallhäll and Barkarbystaden. We carry furniture, appliances and other items regardless of floor – no extra charges for stairs.",
        },
        {
          question: "Do you collect from villas and garages in Järfälla?",
          answer:
            "Yes, a large share of our jobs in Järfälla are villa pickups in Viksjö, Skälby, Barkarby and Stäket. We reverse onto the driveway where possible and clear garages, basements and storage units. Just let us know if a gate or garage door needs opening.",
        },
        {
          question: "Can you disassemble furniture before removal?",
          answer:
            "Yes, we disassemble furniture on site if needed to get it through doors or down stairs. This is included in the service at no extra cost.",
        },
        {
          question: "Is junk removal in Järfälla insured?",
          answer:
            "Yes, all jobs are insured through Trygg Hansa. This means your property, stairwell, driveway and belongings are protected throughout the entire process – from lifting to transport.",
        },
        {
          question: "Where do you take the waste after junk removal in Järfälla?",
          answer:
            "We sort all materials and drive them to the nearest approved recycling centre. Järfälla is one of the part-owner municipalities of SÖRAB, so it is usually Görväln ÅVC by Lake Mälaren. Items that can be reused are donated to Myrorna or Stadsmissionen.",
        },
        {
          question: "Can I book junk removal in Järfälla on weekends?",
          answer:
            "Yes, we offer junk removal every day of the week, including Saturdays and Sundays. Choose the date and time that works best for you directly in our booking.",
        },
        {
          question: "Can you collect items from basements or storage units in Järfälla?",
          answer:
            "Yes, we pick up items from all spaces – basements, storage units, attics and garages. We are used to both the villa storage rooms in Viksjö and the apartment storage units in Jakobsberg and Barkarbystaden.",
        },
        {
          question: "Can you make multiple stops during junk removal in Järfälla?",
          answer:
            "Yes, you can add multiple pickup addresses in the same booking. This is practical if you need to collect items from both your apartment in Jakobsberg and a storage unit at another address in the municipality, for example.",
        },
        {
          question: "Which districts in Järfälla do you cover?",
          answer:
            "We perform junk removal across all of Järfälla – Jakobsberg, Kallhäll, Stäket, Viksjö, Barkarby, Barkarbystaden and Skälby. No extra charges for any district.",
        },
        {
          question: "Do you handle hazardous waste during junk removal in Järfälla?",
          answer:
            "Unfortunately, we cannot transport hazardous waste such as paint, chemicals, batteries or asbestos. As a Järfälla resident you can drop those off for free at Görväln recycling centre.",
        },
        {
          question: "Which municipalities around Järfälla do you serve?",
          answer:
            "We cover the entire Stockholm region, including Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. No extra charges for surrounding municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Book your junk removal in Järfälla in 3 easy steps",
        step2Text:
          "Our young workers in Järfälla come to you and collect whatever you want removed. We handle heavy lifting, tight staircases, narrow elevators and full villa garages – you don't have to lift a finger.",
        ctaHeading: "Book junk removal in Järfälla today",
        ctaText:
          "Fill in the form and get an instant price – no quote requests. Easy junk removal across the whole municipality of Järfälla.",
      },
    },
  },
];
